import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useGetHomeQuery, useCreateWorkflowMutation, useUpdateWorkflowMutation } from "@/store/rest_api";
import { TRIGGER_OPTIONS, REMINDER_TRIGGERS } from "@/components/pages/workflows/WorkflowConstants";
import TriggerConfigEditor from "@/components/pages/workflows/TriggerConfigEditor";
import ActionCard from "@/components/pages/workflows/ActionCard";
import type { ActionFormData } from "@/components/pages/workflows/ActionCard";
import type { Workflow, WorkflowActionConfigValue, WorkflowTriggerType } from "@/model/interface/Workflow";

interface WorkflowFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflow?: Workflow | null;
}

function parseJson(str?: string): Record<string, string> {
  if (!str) return {};
  try { return JSON.parse(str); } catch { return {}; }
}

const WorkflowFormDialog: React.FC<WorkflowFormDialogProps> = ({ open, onOpenChange, workflow }) => {
  const [createWorkflow, { isLoading: isCreating }] = useCreateWorkflowMutation();
  const [updateWorkflow, { isLoading: isUpdating }] = useUpdateWorkflowMutation();

  const isEditing = !!workflow;
  const isSubmitting = isCreating || isUpdating;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [triggerType, setTriggerType] = useState<WorkflowTriggerType>("NEW_CONTACT");
  const [triggerConfig, setTriggerConfig] = useState<Record<string, string>>({});
  const [actions, setActions] = useState<ActionFormData[]>([]);
  const [stopOnReply, setStopOnReply] = useState(true);

  const { data: user } = useGetHomeQuery();
  const autoOutreachDisabled =
    REMINDER_TRIGGERS.includes(triggerType) && !(user?.activeOrganisation?.remindersAutoOutreachEnabled ?? false);

  useEffect(() => {
    if (open) {
      if (workflow) {
        setName(workflow.name);
        setDescription(workflow.description || "");
        setTriggerType(workflow.triggerType);
        setTriggerConfig(parseJson(workflow.triggerConfig));
        setActions(workflow.actions.map((action) => ({
          id: action.id,
          actionType: action.actionType,
          config: parseJson(action.config),
          delayMinutes: action.delayMinutes,
        })));
        setStopOnReply(workflow.stopOnReply ?? false);
      } else {
        setName("");
        setDescription("");
        setTriggerType("NEW_CONTACT");
        setTriggerConfig({});
        setActions([]);
        setStopOnReply(true);
      }
    }
  }, [open, workflow]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }

    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      stopOnReply,
      triggerType,
      triggerConfig: Object.keys(triggerConfig).length > 0 ? JSON.stringify(triggerConfig) : undefined,
      actions: actions.map((action) => ({
        ...(action.id ? { id: action.id } : {}),
        actionType: action.actionType,
        config: Object.keys(action.config).length > 0 ? JSON.stringify(action.config) : undefined,
        delayMinutes: action.delayMinutes,
      })),
    };

    try {
      if (isEditing) {
        await updateWorkflow({ id: workflow!.id, data: payload }).unwrap();
        toast.success("Workflow updated");
      } else {
        await createWorkflow(payload).unwrap();
        toast.success("Workflow created");
      }
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error?.detail ?? `Failed to ${isEditing ? "update" : "create"} workflow`);
    }
  };

  const updateAction = (index: number, updates: Partial<ActionFormData>) => {
    setActions(actions.map((action, actionIndex) => actionIndex === index ? { ...action, ...updates } : action));
  };

  const updateActionConfig = (index: number, key: string, value: WorkflowActionConfigValue) => {
    const action = actions[index];
    updateAction(index, { config: { ...action.config, [key]: value } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Workflow" : "New Workflow"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input placeholder="Workflow name..." value={name} onChange={(event) => setName(event.target.value)} className="mt-1" />
          </div>

          <div>
            <Label>Description</Label>
            <Textarea rows={2} placeholder="Optional description..." value={description} onChange={(event) => setDescription(event.target.value)} className="mt-1" />
          </div>

          <div>
            <Label>Trigger</Label>
            <Select value={triggerType} onValueChange={(value) => { setTriggerType(value as WorkflowTriggerType); setTriggerConfig({}); }}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TRIGGER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-2">
              <TriggerConfigEditor
                triggerType={triggerType}
                config={triggerConfig}
                onChange={setTriggerConfig}
                workflowId={workflow?.id}
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label>Stop when the contact replies</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                An inbound text or call from the contact cancels their remaining steps.
              </p>
            </div>
            <Switch checked={stopOnReply} onCheckedChange={setStopOnReply} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Actions</Label>
              <Button variant="outline" size="sm" onClick={() => setActions([...actions, { actionType: "SEND_SMS", config: {}, delayMinutes: 0 }])}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Action
              </Button>
            </div>

            {actions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No actions. Add at least one action.</p>
            ) : (
              <div className="space-y-3">
                {actions.map((action, index) => (
                  <ActionCard
                    key={index}
                    action={action}
                    index={index}
                    triggerType={triggerType}
                    autoOutreachDisabled={autoOutreachDisabled}
                    onUpdate={(updates) => updateAction(index, updates)}
                    onUpdateConfig={(key, value) => updateActionConfig(index, key, value)}
                    onRemove={() => setActions(actions.filter((_, actionIndex) => actionIndex !== index))}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEditing ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowFormDialog;
